<script lang="ts">
	import { yyyy } from "./store.svelte";
	import { toast } from "./lib/toast.svelte.ts";
	import { onMount } from "svelte";
	import { fly } from "svelte/transition";
	import Fa from "svelte-fa";
	import { faFilePdf } from "@fortawesome/free-solid-svg-icons";

	import { savePdf } from "./lib/savePdf";
	import Logo from "./Logo.svelte";

	let invoice: any = $state(null);
	let error = $state(false);
	let ready = $state(false);
	let saving = $state(false);
	let band1: number = $derived(Math.min(invoice?.user_count ?? 0, 25));
	let band2: number = $derived(Math.max((invoice?.user_count ?? 0) - 25, 0));
	let storageOverage: number = $derived(Math.max((invoice?.storage_gb ?? 0) - 100, 0));
	let cardEl: HTMLElement;

	let showLogo = $state(false);

	let downloadPdf = async () => {
		saving = true;
		showLogo = true;
		// Let the DOM render the logo before capturing
		await new Promise(r => setTimeout(r, 100));
		try {
			await savePdf(cardEl, `invoice-${invoice.invoice_id}.pdf`);
		} finally {
			saving = false;
			showLogo = false;
		}
	};

	let formatCurrency = (val: number) => {
		return "$" + (val ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	};

	let formatDate = (d: string) => {
		const s = d.includes('T') ? d : d + 'T00:00:00';
		return new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
	};

	let dueDate = (d: string) => {
		const s = d.includes('T') ? d : d + 'T00:00:00';
		const dt = new Date(s);
		dt.setDate(dt.getDate() + 14);
		return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
	};

	onMount(async () => {
		const token = window.location.pathname.split("/invoice/")[1];
		if (!token) { error = true; ready = true; return; }

		const res = await fetch(yyyy.uri.api + "/rpc/view_invoice", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ token }),
		});
		if (res.ok) {
			const data = await res.json();
			if (data) { invoice = data; } else { error = true; }
		} else {
			error = true;
		}
		ready = true;

		if (invoice && yyyy.params?.has('download')) {
			await new Promise(r => setTimeout(r, 1000));
			downloadPdf();
			toast.info("Your PDF download will start shortly", 3000, "top-center");
		}
	});
</script>

{#if ready}
	<div class="column is-two-thirds not-too-wide invoice-wide">
		<div class="card lvwr-card" bind:this={cardEl}>
			{#if showLogo}
				<div class="invoice-side-logo">
					<Logo usage="logo" interact={false} />
				</div>
			{/if}
			<div class="card-content lvwr-card-content" transition:fly|local={{ y: -200 }}>
				{#if error}
					<p style="padding: 60px 0; text-align: center; color: #999;">Invoice not found.</p>
				{:else if invoice}
					<div class="invoice-header">
						<span class="invoice-brand">Livewire</span>
						<span class="invoice-label">INVOICE</span>
					</div>
					<div class="invoice-meta">
						<div>
							<strong class="invoice-company">{invoice.company_name}</strong>
							{#if invoice.address}
								<br><span class="invoice-address">{invoice.address}</span>
							{/if}
						</div>
						<div class="invoice-meta-right">
							<strong>Invoice #</strong> {invoice.invoice_id}<br>
							<strong>Period</strong> {formatDate(invoice.period_start)} — {formatDate(invoice.period_end)}<br>
							<strong>Issued</strong> {formatDate(invoice.created_at)}<br>
							<strong>Due</strong> {dueDate(invoice.created_at)}
						</div>
					</div>
					<table class="invoice-table">
						<thead>
							<tr>
								<th class="has-text-left">Item</th>
								<th class="has-text-right">Qty</th>
								<th class="has-text-right">Rate</th>
								<th class="has-text-right">Amount</th>
							</tr>
						</thead>
						<tbody>
							<tr class="total-users-row">
								<td><strong>Total Users: {invoice.user_count ?? 0}</strong></td>
								<td></td>
								<td></td>
								<td class="has-text-right"><strong>{formatCurrency(invoice.user_charge)}</strong></td>
							</tr>
							<tr class="band-detail-row">
								<td>&nbsp;&nbsp;Users (1-25)</td>
								<td class="has-text-right">{band1}</td>
								<td class="has-text-right">$179/user</td>
								<td class="has-text-right">{formatCurrency(band1 * 179)}</td>
							</tr>
							{#if band2 > 0}
								<tr class="band-detail-row">
									<td>&nbsp;&nbsp;Users (26-75)</td>
									<td class="has-text-right">{band2}</td>
									<td class="has-text-right">$129/user</td>
									<td class="has-text-right">{formatCurrency(band2 * 129)}</td>
								</tr>
							{/if}
							<tr>
								<td>Livewires <small>(info only)</small></td>
								<td class="has-text-right">{invoice.livewire_count ?? ''}</td>
								<td class="has-text-right">—</td>
								<td class="has-text-right">—</td>
							</tr>
							<tr>
								<td>Storage <small>(100 GB included)</small></td>
								<td class="has-text-right">{invoice.storage_gb != null ? Number(invoice.storage_gb).toFixed(2) + ' GB' : ''}</td>
								<td class="has-text-right">{storageOverage > 0 ? '$5/GB' : 'Free'}</td>
								<td class="has-text-right">{storageOverage > 0 ? formatCurrency(invoice.storage_charge) : '$0.00'}</td>
							</tr>
							{#each invoice.addon_details ?? [] as ad}
								<tr>
									<td>{ad.name}{#if ad.days_active < ad.total_days} <small>({ad.days_active}/{ad.total_days} days)</small>{/if}</td>
									<td class="has-text-right"></td>
									<td class="has-text-right"></td>
									<td class="has-text-right">{formatCurrency(ad.charge)}</td>
								</tr>
							{/each}
							{#each invoice.line_items ?? [] as li}
								<tr>
									<td>{li.description}</td>
									<td class="has-text-right"></td>
									<td class="has-text-right"></td>
									<td class="has-text-right">{formatCurrency(li.amount)}</td>
								</tr>
							{/each}
							{#if invoice.discount > 0}
								<tr class="discount-row">
									<td>Discount</td>
									<td></td>
									<td></td>
									<td class="has-text-right">-{formatCurrency(invoice.discount)}</td>
								</tr>
							{/if}
						</tbody>
						<tfoot>
							<tr>
								<td>Total</td>
								<td></td>
								<td></td>
								<td class="has-text-right">{formatCurrency(invoice.total)}</td>
							</tr>
						</tfoot>
					</table>
					<div class="invoice-footer">
						<a href="https://lvwr.io">lvwr.io</a>
						<span class="sep">|</span>
						<a href="mailto:billing@lvwr.io">billing@lvwr.io</a>
					</div>
				{:else}
					<p style="padding: 60px 0; text-align: center; color: #999;">Loading...</p>
				{/if}
			</div>
		</div>
		<div class="print-action">
			<button class="button is-small is-link" class:is-loading={saving} onclick={downloadPdf} disabled={saving}>
				{#if !saving}<Fa icon={faFilePdf} />&nbsp;{/if}Download PDF
			</button>
		</div>
	</div>
{/if}

<style>
	.invoice-wide {
		max-width: 800px;
	}

	.lvwr-card {
		position: relative;
		overflow: hidden;
	}

	.invoice-side-logo {
		position: absolute;
		left: -30px;
		bottom: 30px;
		width: 80px;
		transform: rotate(90deg);
		transform-origin: center center;
		opacity: 0.75;
	}

	.invoice-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding-bottom: 16px;
		border-bottom: 1px solid #eee;
	}

	.invoice-brand {
		color: #ff3e00;
		font-family: Ubuntu, Inter, SF Pro, Segoe UI, Roboto, Helvetica Neue, Helvetica, Arial, sans-serif;
		font-size: 24px;
		font-weight: 300;
		text-transform: uppercase;
		letter-spacing: 2px;
	}

	.invoice-label {
		font-size: 13px;
		color: #999;
	}

	.invoice-meta {
		display: flex;
		justify-content: space-between;
		padding: 20px 0 0 0;
	}

	.invoice-company {
		font-size: 16px;
	}

	.invoice-address {
		color: #666;
		font-size: 13px;
		white-space: pre-line;
	}

	.invoice-meta-right {
		text-align: right;
		font-size: 13px;
		color: #666;
	}

	.invoice-meta-right strong {
		color: #333;
	}

	.invoice-table {
		width: 100%;
		border-collapse: collapse;
		margin: 20px 0 0 0;
	}

	.invoice-table thead tr {
		border-bottom: 2px solid #eee;
	}

	.invoice-table th {
		padding: 8px 0;
		font-size: 13px;
		text-transform: uppercase;
		color: #999;
		letter-spacing: 0.5px;
		font-weight: 600;
	}

	.invoice-table tbody td {
		padding: 8px 0;
		border-bottom: 1px solid #f0f0f0;
	}

	.invoice-table .discount-row td {
		color: #48c774;
	}

	.invoice-table tfoot td {
		padding: 12px 0;
		font-weight: 700;
		font-size: 16px;
		border-top: 2px solid #eee;
	}

	.invoice-footer {
		padding: 20px 0;
		border-top: 1px solid #eee;
		text-align: center;
		font-size: 14px;
		margin-top: 16px;
	}

	.invoice-footer a {
		color: #ff3e00;
		text-decoration: none;
	}

	.invoice-footer a:last-child {
		color: #666;
	}

	.invoice-footer .sep {
		color: #ccc;
		margin: 0 10px;
	}

	.print-action {
		text-align: center;
		margin-top: 1rem;
	}

	:global(.band-detail-row td) {
		color: #888 !important;
		font-size: 0.85em;
	}
</style>
